<template>
  <div :style="wrapperStyle" class="tracking-wrapper">
    <svg :width="sizePx" :height="sizePx" :viewBox="`0 0 100 100`" class="tracking-svg">
      <g v-for="(ring, index) in rings" :key="index">
        <circle
          class="tracking-bg"
          :class="{'dark' : isDark, 'darkRing' : darkRing}"
          :r="ring.radius"
          cx="50"
          cy="50"
          fill="transparent"
          :stroke-width="strokeWidth"
        />
        <circle
          :class="[ring.className, fill]"
          :r="ring.radius"
          cx="50"
          cy="50"
          fill="transparent"
          stroke-linecap="round"
          :stroke-width="strokeWidth"
          :stroke-dasharray="ring.circumference"
          :stroke-dashoffset="ring.dashOffset"
        />
      </g>
    </svg>
    <div v-if="profileImage && !hideProfile" class="tracking-profile">
      <img :src="profileImage" onerror="this.style.display='none'"/>
    </div>
  </div>
</template>

<script>
// import API from 'app/axios';
export default {
  data() {
    return {
      strokeWidth: 3.5,
      demo: {
        time: 0,
        topics: 0, 
        locales: 0,
      },
    }
  },

  props: {
    size: {
      type: Number,
      default: 65
    },
    progress: {
      type: Object,
      default: null
    },
    fixed: {
      type: Object,
      default: null
    },
    focus: {
      type: String,
      default: null,
    },
    mode: {
      type: String,
      default: null,
    },
    hideProfile: {
      type: Boolean,
      default: false
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    darkRing: {
      type: Boolean,
      default: false,
    },
    fill: {
      type: String,
      default: null,
    }
  },

  mounted() {
    if (this.focus && this.isDemo) {
      this.demo[this.focus] = 0;
      window.setTimeout(() => { this.demoUpdate(this.focus); }, 100);
    }
  },

  computed: {
    sizePx() {
      return `${this.size}px`;
    },

    unlocked() {
      return this.$userEngine.dialUnlocked || []
    },

    data() {
      return this.$store.getters.getLibraryItem('dial') || {};
    },

    profileImage() {
      return this.$store.getters.getAccount?.details?.profilePicture || '';
    },

    isDemo() {
      return (this.mode == 'demo');
    },

    isDisplay() {
      return (this.mode == 'display');
    },

    ringBaseData() {
      // are we in "demo-mode" ..? default to that
      if (this.isDemo) {
        return this.demo;        
      }

      // are we showing fixed data .. (like the calendar)
      if (this.fixed !== null) {
        return {
          time: Math.min(100, Math.max(0, this.fixed?.rings?.time?.px || 0)),
          topics: Math.min(100, Math.max(0, this.fixed?.rings?.topics?.px || 0)),
          locales: Math.min(100, Math.max(0, this.fixed?.rings?.locales?.px || 0)),
        }
      }

      // otherwise ... build a combo of database + progress data..
      return {        
        time: Math.min(100, Math.max(0, (this.data?.rings?.time?.px || 0) + (this.progress?.time || 0))),
        topics: Math.min(100, Math.max(0, (this.data?.rings?.topics?.px || 0) + (this.progress?.topics || 0))),
        locales: Math.min(100, Math.max(0, (this.data?.rings?.locales?.px || 0) + (this.progress?.locales || 0)))
      };
    },

    ringValues() { 
      // we might be rendering various different things..
      let output = this.ringBaseData;

      // console.log('Base', output, ', Focus: ', this.focus);
      
      // allow singular focus (as a prop - for the template to determine) .. or use the user's settings
      let aShowTracks = [];
      // for dashboard specific focus..
      if (this.focus) {
        // but only if we've unlocked this dashboard -- OR explicitly in demo-mode... 
        if (this.unlocked.includes(this.focus) || this.isDemo || this.isDisplay) {
          aShowTracks = [this.focus];
        }
      } else {
        aShowTracks = this.unlocked;
      }
      
      let redacted = {};
      Object.entries(output).map(([key, value]) => {
        // if (key == this.focus) {
        if (aShowTracks.includes(key)) {
          redacted[key] = value;
          return;
        }
        redacted[key] = 0;
      });
      
      // console.log(`  OUTPUT: (${this.focus})`,  Object.values(redacted), ' :: ', this.progress, output,this.unlocked, aShowTracks, redacted, this.data?.rings);
      return Object.values(redacted);
    },

    rings() {
      const baseRadius = 40;
      const classNames = ['ring-outer', 'ring-mid', 'ring-inner'];
      let output = this.ringValues.map((value, index) => {
        const radius = baseRadius - index * (this.strokeWidth + this.strokeWidth);
        const circumference = 2 * Math.PI * radius;
        return {
          radius,
          circumference,
          dashOffset: circumference * (1 - (value/100)),
          className: classNames[index] || ''
        };
      });
      // console.log('OUTPUT', output);
      return output;
    },

    wrapperStyle() {
      return {
        width: this.sizePx,
        height: this.sizePx,
        position: 'relative',
        display: 'inline-block'
      };
    }
  },

  methods: {
    updateRingValueProgress() {
      this.data.time.px = Math.max(0, (this.data?.rings?.time?.px || 0) - (this.progress?.time || 0));
      this.data.topics.px = Math.max(0, (this.data?.rings?.topics?.px || 0) - (this.progress?.topics || 0));
      this.data.locales.px = Math.max(0, (this.data?.rings?.locales?.px || 0) - (this.progress?.locales || 0));
    },

    demoUpdate(sFocus) {
      let iDelay = Math.random() * 3;
      let iIncrement = (Math.round(Math.random() * 15) + 15);
      this.demo[sFocus] += iIncrement;
      
      // okay.. but if we're in the odd space (trailing circle) go full circle again! :D
      if (Math.floor(this.demo[sFocus] / 100) % 2) {
        this.demo[sFocus] += 100;
      }

      window.setTimeout(() => { this.demoUpdate(this.focus); }, iDelay * 1000);
    }
  },
};
</script>

<style lang="scss" scoped>
  @import 'src/assets/scss/v5.scss';

  .tracking-wrapper {
    position: relative;
    display: inline-block;

    .tracking-svg {
      transform: rotate(-90deg);

      .tracking-bg {
        opacity: 1;
        stroke: rgba($neutral-100, 0.1);

        &.dark,
        .darkmode & {
          stroke: rgba($neutral-600, 0.1);
        }

        &.darkRing {
          stroke: rgba($neutral-100, 0.1);

          .darkmode & {
            stroke: rgba($neutral-100, 0.1);
          }
        }
      }

      .ring-outer,
      .ring-mid,
      .ring-inner {
        transition: stroke-dashoffset 1s ease;
      }

      .ring-outer {
        stroke: $time-tone;
      }

      .ring-mid {
        stroke: $topics-tone;
      }

      .ring-inner {
        stroke: $locales-tone;
      }

      .black {
        stroke: $neutral-100;
      }

      .white {
        stroke: $neutral-600;
      }
    }

    .tracking-profile {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      width: 40%;
      height: 40%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }
  }
</style>
